/**
 * Scroll Animation Component
 *
 * Automatically triggers fade-in animations when elements scroll into view.
 * See README-video-player.md for usage details.
 *
 */

interface ScrollAnimateOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  once?: boolean;
}

class ScrollAnimationController {
  private readonly observer: IntersectionObserver;
  private readonly elements: Set<Element> = new Set();
  private readonly options: ScrollAnimateOptions;

  constructor(options: ScrollAnimateOptions = {}) {
    this.options = {
      root: null,
      rootMargin: '0px 0px -10% 0px', // Trigger when 10% of element is visible
      threshold: 0.1,
      once: true,
      ...options,
    };

    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
      root: this.options.root,
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold,
    });

    this.init();
  }

  private init(): void {
    // Find all elements with scroll-animate class
    this.discoverElements();

    // Set up mutation observer to handle dynamically added elements
    this.setupMutationObserver();
  }

  private discoverElements(): void {
    const elements = document.querySelectorAll('.scroll-animate');
    for (const element of Array.from(elements)) {
      this.observeElement(element);
    }
  }

  private observeElement(element: Element): void {
    if (!this.elements.has(element)) {
      this.elements.add(element);
      this.observer.observe(element);
    }
  }

  private unobserveElement(element: Element): void {
    if (this.elements.has(element)) {
      this.elements.delete(element);
      this.observer.unobserve(element);
    }
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const element = entry.target;

      if (entry.isIntersecting) {
        // Element is now visible, trigger animation
        this.triggerAnimation(element);

        // If `once` is true, stop observing this element
        if (this.options.once) {
          this.unobserveElement(element);
        }
      } else if (!this.options.once) {
        // Element is no longer visible and we want to re-animate
        this.resetAnimation(element);
      }
    }
  }

  private triggerAnimation(element: Element): void {
    // Add the visible class to trigger CSS animation
    element.classList.add('scroll-animate--visible');

    // Dispatch custom event for potential additional functionality
    element.dispatchEvent(
      new CustomEvent('scroll-animate:visible', {
        bubbles: true,
        detail: { element },
      })
    );
  }

  private resetAnimation(element: Element): void {
    // Remove the visible class to reset animation
    element.classList.remove('scroll-animate--visible');

    // Dispatch custom event
    element.dispatchEvent(
      new CustomEvent('scroll-animate:hidden', {
        bubbles: true,
        detail: { element },
      })
    );
  }

  private setupMutationObserver(): void {
    const mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        // Check for newly added nodes
        for (const node of Array.from(mutation.addedNodes)) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            // Check if the added element itself has scroll-animate class
            if (element.classList.contains('scroll-animate')) {
              this.observeElement(element);
            }

            // Check for scroll-animate elements within the added element
            const scrollAnimateElements = element.querySelectorAll('.scroll-animate');
            for (const scrollElement of Array.from(scrollAnimateElements)) {
              this.observeElement(scrollElement);
            }
          }
        }

        // Handle removed nodes
        for (const node of Array.from(mutation.removedNodes)) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            if (element.classList.contains('scroll-animate')) {
              this.unobserveElement(element);
            }

            const scrollAnimateElements = element.querySelectorAll('.scroll-animate');
            for (const scrollElement of Array.from(scrollAnimateElements)) {
              this.unobserveElement(scrollElement);
            }
          }
        }
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Public methods for manual control
  public refresh(): void {
    this.discoverElements();
  }

  public destroy(): void {
    this.observer.disconnect();
    this.elements.clear();
  }

  public observeNewElement(element: Element): void {
    this.observeElement(element);
  }
}

// Initialize the scroll animation controller when DOM is ready
function initScrollAnimations(): void {
  // Check if user prefers reduced motion
  const prefersReducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!prefersReducedMotion) {
    new ScrollAnimationController({
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1,
      once: true,
    });
  } else {
    // For users who prefer reduced motion, immediately show all elements
    const elements = document.querySelectorAll('.scroll-animate');
    for (const element of Array.from(elements)) {
      element.classList.add('scroll-animate--visible');
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollAnimations);
} else {
  initScrollAnimations();
}

// Export for manual initialization if needed
export { ScrollAnimationController, initScrollAnimations };
